import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import {
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Copy,
  Mail,
  Sparkles
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { marketingAPI, EmailTemplate } from '../../lib/marketing-api';
import { toast } from 'sonner';
import { logger } from '../../services/logger.service';

// Pre-built template library
const TEMPLATE_LIBRARY: Partial<EmailTemplate>[] = [
  {
    name: 'Welcome New Lead',
    subject: 'Welcome to Something Property - Your Property Journey Starts Here',
    content: `Hi {{firstName}},

Thank you for your interest in Something Property! We're thrilled to help you find your perfect property.

Our team of expert agents is ready to assist you in finding properties that match your needs and budget. Whether you're looking to buy or rent, we have exclusive listings across South Africa's most sought-after locations.

What happens next:
• One of our agents will contact you within 24 hours
• We'll understand your property requirements
• You'll receive personalized property recommendations

In the meantime, feel free to browse our latest listings on our website.

Best regards,
The Something Property Team`,
    category: 'welcome' as const,
    variables: ['firstName']
  },
  {
    name: 'Follow-up After Viewing',
    subject: 'Thank you for viewing {{propertyName}}',
    content: `Hi {{firstName}},

Thank you for taking the time to view {{propertyName}} with us today!

We hope you enjoyed the property. We'd love to hear your thoughts and answer any questions you may have.

Property Details:
• Location: {{propertyLocation}}
• Price: R {{propertyPrice}}
• Bedrooms: {{bedrooms}} | Bathrooms: {{bathrooms}}

If you're interested in this property or would like to schedule another viewing, please don't hesitate to reach out.

We're also happy to show you similar properties that might interest you.

Best regards,
{{agentName}}
Something Property`,
    category: 'follow-up' as const,
    variables: ['firstName', 'propertyName', 'propertyLocation', 'propertyPrice', 'bedrooms', 'bathrooms', 'agentName']
  },
  {
    name: 'New Property Alert',
    subject: 'New Property Match: {{propertyName}}',
    content: `Hi {{firstName}},

Great news! We have a new property that matches your search criteria:

{{propertyName}}
📍 {{propertyLocation}}
💰 R {{propertyPrice}}
🛏️ {{bedrooms}} Bedrooms | 🚿 {{bathrooms}} Bathrooms
📐 {{propertySize}} sqm

Key Features:
{{propertyFeatures}}

This property just hit the market and we think it's perfect for you based on your preferences.

Would you like to schedule a viewing? Click the link below or reply to this email:
[Schedule Viewing]

Act fast - properties like this don't stay available for long!

Best regards,
{{agentName}}
Something Property`,
    category: 'property-alert' as const,
    variables: ['firstName', 'propertyName', 'propertyLocation', 'propertyPrice', 'bedrooms', 'bathrooms', 'propertySize', 'propertyFeatures', 'agentName']
  },
  {
    name: 'Monthly Newsletter',
    subject: 'Something Property Monthly Update - {{month}}',
    content: `Hi {{firstName}},

Welcome to this month's property market update!

📊 Market Insights:
• Average property prices: {{avgPrice}}
• New listings this month: {{newListings}}
• Market trend: {{marketTrend}}

🏆 Featured Properties:
{{featuredProperties}}

💡 Expert Tips:
{{expertTips}}

📅 Upcoming Events:
We're hosting exclusive property viewings and open houses. Register now to secure your spot!

Stay connected with us on social media for daily property updates and market insights.

Happy house hunting!

The Something Property Team`,
    category: 'newsletter' as const,
    variables: ['firstName', 'month', 'avgPrice', 'newListings', 'marketTrend', 'featuredProperties', 'expertTips']
  },
  {
    name: 'Exclusive Offer',
    subject: '🌟 Exclusive Property Deal Just for You!',
    content: `Hi {{firstName}},

As a valued member of our Something Property community, we're offering you exclusive early access to a premium property:

✨ EXCLUSIVE OPPORTUNITY ✨

{{propertyName}}
Location: {{propertyLocation}}
Special Price: R {{specialPrice}} (was R {{originalPrice}})
Savings: R {{savings}}

This exclusive offer is available only to select clients and expires in {{expiryDays}} days.

Why this property?
{{propertyHighlights}}

Ready to seize this opportunity?
[Schedule Private Viewing] | [Learn More]

Don't miss out on this incredible deal!

Best regards,
{{agentName}}
Something Property

P.S. This offer is strictly confidential and available to you only.`,
    category: 'promotional' as const,
    variables: ['firstName', 'propertyName', 'propertyLocation', 'specialPrice', 'originalPrice', 'savings', 'expiryDays', 'propertyHighlights', 'agentName']
  }
];

export function EmailTemplates() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showLibraryDialog, setShowLibraryDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    content: '',
    category: 'custom' as EmailTemplate['category'],
    variables: [] as string[]
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const { templates: data } = await marketingAPI.getEmailTemplates();
      setTemplates(data);
    } catch (error) {
      logger.error('Failed to load templates', error as Error, undefined, 'EmailTemplates');
      toast.error('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      await marketingAPI.createEmailTemplate(formData);
      toast.success('Template created successfully');
      setShowAddDialog(false);
      resetForm();
      loadTemplates();
    } catch (error) {
      logger.error('Failed to create template', error as Error, undefined, 'EmailTemplates');
      toast.error('Failed to create template');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await marketingAPI.updateEmailTemplate(editingTemplate.id, formData);
      toast.success('Template updated successfully');
      setEditingTemplate(null);
      resetForm();
      loadTemplates();
    } catch (error) {
      logger.error('Failed to update template', error as Error, undefined, 'EmailTemplates');
      toast.error('Failed to update template');
    }
  };

  const handleDuplicateTemplate = async (template: EmailTemplate) => {
    try {
      await marketingAPI.createEmailTemplate({
        name: `${template.name} (Copy)`,
        subject: template.subject,
        content: template.content,
        category: template.category,
        variables: template.variables
      });
      toast.success('Template duplicated successfully');
      loadTemplates();
    } catch (error) {
      logger.error('Failed to duplicate template', error as Error, undefined, 'EmailTemplates');
      toast.error('Failed to duplicate template');
    }
  };

  const handleUseLibraryTemplate = async (template: Partial<EmailTemplate>) => {
    try {
      await marketingAPI.createEmailTemplate(template);
      toast.success('Template added from library');
      setShowLibraryDialog(false);
      loadTemplates();
    } catch (error) {
      logger.error('Failed to add template from library', error as Error, undefined, 'EmailTemplates');
      toast.error('Failed to add template');
    }
  };

  const openEditDialog = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      content: template.content,
      category: template.category,
      variables: template.variables
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subject: '',
      content: '',
      category: 'custom',
      variables: []
    });
  };

  const extractVariables = (text: string): string[] => {
    const regex = /{{([^}]+)}}/g;
    const matches = text.match(regex);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.replace(/{{|}}/g, '')))];
  };

  const handleContentChange = (content: string) => {
    setFormData({
      ...formData,
      content,
      variables: extractVariables(content + formData.subject)
    });
  };

  const getCategoryBadge = (category: EmailTemplate['category']) => {
    const variants: Record<EmailTemplate['category'], string> = {
      welcome: 'bg-blue-500',
      'follow-up': 'bg-green-500',
      'property-alert': 'bg-yellow-500',
      newsletter: 'bg-purple-500',
      promotional: 'bg-orange-500',
      custom: 'bg-gray-500'
    };
    
    return (
      <Badge className={variants[category]}>
        {category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
      </Badge>
    );
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-6 h-6 text-primary" />
            Email Templates
          </h2>
          <p className="text-muted-foreground mt-1">
            Create and manage reusable email templates
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowLibraryDialog(true)} className="gap-2" data-testid="marketing-template-library-button">
            <Sparkles className="w-4 h-4" />
            Template Library
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2" data-testid="marketing-template-create-button">
            <Plus className="w-4 h-4" />
            Create Template
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Templates</p>
                <p className="text-2xl font-bold">{templates.length}</p>
              </div>
              <FileText className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Custom Templates</p>
                <p className="text-2xl font-bold">
                  {templates.filter(t => t.category === 'custom').length}
                </p>
              </div>
              <Edit className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pre-built</p>
                <p className="text-2xl font-bold">
                  {templates.filter(t => t.category !== 'custom').length}
                </p>
              </div>
              <Sparkles className="w-8 h-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="welcome">Welcome</SelectItem>
                <SelectItem value="follow-up">Follow-up</SelectItem>
                <SelectItem value="property-alert">Property Alert</SelectItem>
                <SelectItem value="newsletter">Newsletter</SelectItem>
                <SelectItem value="promotional">Promotional</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            Loading templates...
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            No templates found. Create your first template!
          </div>
        ) : (
          filteredTemplates.map((template) => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <Card className="hover:border-primary/50 transition-colors h-full">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <CardTitle className="text-base line-clamp-1">{template.name}</CardTitle>
                    {getCategoryBadge(template.category)}
                  </div>
                  <CardDescription className="line-clamp-2">{template.subject}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm text-muted-foreground line-clamp-3">
                    {template.content}
                  </div>
                  {template.variables.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {template.variables.slice(0, 3).map((variable) => (
                        <Badge key={variable} variant="outline" className="text-xs">
                          {`{{${variable}}}`}
                        </Badge>
                      ))}
                      {template.variables.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{template.variables.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewTemplate(template)}
                      className="flex-1"
                      data-testid="marketing-template-preview-button"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(template)}
                      data-testid="marketing-template-edit-button"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicateTemplate(template)}
                      data-testid="marketing-template-duplicate-button"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Template Library Dialog */}
      <Dialog open={showLibraryDialog} onOpenChange={setShowLibraryDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Template Library
            </DialogTitle>
            <DialogDescription>
              Choose from our pre-built professional email templates
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {TEMPLATE_LIBRARY.map((template, index) => (
              <Card key={index} className="border-border/50">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <CardDescription className="mt-1">{template.subject}</CardDescription>
                    </div>
                    {template.category && getCategoryBadge(template.category)}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-4 whitespace-pre-line">
                    {template.content}
                  </p>
                  <Button
                    size="sm"
                    onClick={() => handleUseLibraryTemplate(template)}
                    className="gap-2"
                    data-testid="marketing-template-use-library-button"
                  >
                    <Plus className="w-4 h-4" />
                    Use This Template
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLibraryDialog(false)} data-testid="marketing-template-library-close-button">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Template Dialog */}
      <Dialog open={showAddDialog || editingTemplate !== null} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setEditingTemplate(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Email Template'}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate ? 'Update template details' : 'Create a new reusable email template'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Welcome Email"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value: EmailTemplate['category']) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="welcome">Welcome</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                  <SelectItem value="property-alert">Property Alert</SelectItem>
                  <SelectItem value="newsletter">Newsletter</SelectItem>
                  <SelectItem value="promotional">Promotional</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject">Email Subject *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                placeholder="e.g., Welcome to Something Property!"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="content">Email Content *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => handleContentChange(e.target.value)}
                placeholder="Write your email content here... Use {{variableName}} for dynamic content."
                rows={12}
                required
              />
              <p className="text-xs text-muted-foreground">
                Use double curly braces for variables, e.g., {`{{firstName}}, {{propertyName}}`}
              </p>
            </div>
            {formData.variables.length > 0 && (
              <div className="space-y-2">
                <Label>Detected Variables</Label>
                <div className="flex flex-wrap gap-2">
                  {formData.variables.map((variable) => (
                    <Badge key={variable} variant="secondary">
                      {`{{${variable}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setEditingTemplate(null);
                resetForm();
              }}
              data-testid="marketing-template-cancel-button"
            >
              Cancel
            </Button>
            <Button onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate} data-testid="marketing-template-save-button">
              {editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewTemplate !== null} onOpenChange={(open) => {
        if (!open) setPreviewTemplate(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              Preview your email template
            </DialogDescription>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Subject</Label>
                <p className="text-sm font-semibold mt-1">{previewTemplate.subject}</p>
              </div>
              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground">Content</Label>
                <div className="bg-muted/30 p-4 rounded-lg mt-2 whitespace-pre-wrap text-sm">
                  {previewTemplate.content}
                </div>
              </div>
              {previewTemplate.variables.length > 0 && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-muted-foreground">Variables ({previewTemplate.variables.length})</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {previewTemplate.variables.map((variable) => (
                      <Badge key={variable} variant="outline">
                        {`{{${variable}}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setPreviewTemplate(null)} data-testid="marketing-template-preview-close-button">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
